import React, { useContext } from "react";
import { SearchX } from "lucide-react";
import AppContext from "../../context/AppContext";
import Tooltip from "./Tooltip";
import CommonTooltip from "./CommonTooltip";
import translations from "./Translations";

// Same badge shapes as ReportFailedStatus / the "generating" badge.
const shapeClasses = {
  pill: "rounded-full px-2.5 py-1 shadow-xs",
  chip: "rounded-lg px-3 py-1.5",
};

// Neutral "No records found" badge for a report whose filters matched nothing.
// It finished normally, so it is informational (not red like a failure) and has
// nothing to click -- the tooltip carries the backend message.
// `useCommonTooltip` swaps the hover bubble for CommonTooltip, for screens that
// use it for every tooltip (e.g. the inventory export modal).
export default function ReportNoRecordsStatus({
  message,
  label,
  variant = "pill",
  className = "",
  useCommonTooltip = false,
}) {
  const { theme, langMode } = useContext(AppContext);
  const tooltip =
    (typeof message === "string" && message.trim()) || translations[langMode].reportNoRecordsTooltip;

  const badge = (
    <span
      data-testid="report-no-records-badge"
      className={`inline-flex items-center gap-1.5 border text-xs font-medium whitespace-nowrap ${
        shapeClasses[variant] ?? shapeClasses.pill
      } ${
        theme === "dark"
          ? "border-slate-500/30 bg-slate-500/15 text-slate-300"
          : theme === "high-contrast"
            ? "high-contrast border-2 font-bold"
            : "border-slate-200 bg-slate-50 text-slate-600"
      }`}
    >
      <SearchX size={14} className="shrink-0 text-slate-400" />
      <span>
        {label ? `${label} – ` : ""}
        {translations[langMode].reportNoRecords}
      </span>
    </span>
  );

  return (
    <div className={`inline-flex items-center ${className}`}>
      {useCommonTooltip ? (
        <CommonTooltip
          text={tooltip}
          position="top"
          disableDefaultDescription
          className="cursor-default"
          tooltipClassName="w-max max-w-[min(15rem,calc(100vw-1.5rem))] [text-wrap:balance]"
          fitContent
          clampToViewport
          hoverOnly
        >
          {badge}
        </CommonTooltip>
      ) : (
        <Tooltip text={tooltip} position="top">
          {badge}
        </Tooltip>
      )}
    </div>
  );
}
